/**
 * PII Breach Monitor - Frontend Application
 * 
 * Design Philosophy: Apple Human Interface Guidelines (HIG)
 * - Clarity: Text is legible, icons are precise, functionality is obvious
 * - Deference: UI helps users understand and interact with content
 * - Depth: Visual layers and realistic motion convey hierarchy
 * 
 * GDPR & Privacy Compliance:
 * - User data is never stored locally or in browser storage
 * - All data exists only in component state during the session
 * - No cookies, no tracking, no persistence
 * 
 * Freemium Model:
 * - Free tier: Email breach checks via public APIs
 * - Paid tier: Username, phone, advanced features (stubbed for now)
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import AppLayout from './components/AppLayout';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import BreachCheck from './pages/BreachCheck';
import IdentityRecovery from './pages/IdentityRecovery';
import SecurityHub from './pages/SecurityHub';
import NewsFeed from './components/NewsFeed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletion from './pages/DataDeletion';
import ToolsDevelopment from './pages/ToolsDevelopment';

// Custom theme inspired by "Virtus Pre Nummis" coat of arms
// Navy blue (#0d3b66) and gold (#d4af37) color scheme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0d3b66', // Navy blue from coat of arms
      dark: '#082844',
      light: '#1a4d7a',
    },
    secondary: {
      main: '#d4af37', // Gold from coat of arms
      dark: '#b8941f',
      light: '#e0c054',
    },
    error: {
      main: '#c41e3a', // Deep red from eagle detail
    },
    warning: {
      main: '#d4af37',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12, // Apple-style rounded corners
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', // Apple HIG: sentence case, not all caps
          fontWeight: 600,
          padding: '10px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
  },
});

// Export theme for use in other components
export { theme };

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<AppLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="services" element={<Services />} />
          <Route path="breach-check" element={<BreachCheck />} />
          <Route path="identity-recovery" element={<IdentityRecovery />} />
          <Route path="security-hub" element={<SecurityHub />} />
          <Route path="news" element={<NewsFeed />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-of-service" element={<TermsOfService />} />
          <Route path="data-deletion" element={<DataDeletion />} />
          <Route path="tools-development" element={<ToolsDevelopment />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default App;
